import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { useCachedQuery } from "./useCachedQuery";
import type { MutationResult, TenantBillingSummary } from "../types/billing";

const billingUrl = (tenantId: string): string =>
  `/api/claws/${encodeURIComponent(tenantId)}/billing`;

export type CustomerBillingStatus = "loading" | "ready" | "error";

export type PortalResult =
  | { readonly ok: true; readonly url: string }
  | { readonly ok: false; readonly message: string };

export type StartCheckoutResult =
  | { readonly ok: true; readonly url: string }
  | { readonly ok: false; readonly message: string };

export type UseCustomerBillingResult = {
  readonly status: CustomerBillingStatus;
  readonly summary: TenantBillingSummary | null;
  readonly error: string | null;
  readonly isFetching: boolean;
  readonly refetch: () => Promise<void>;
  readonly startSlotCheckout: (returnTo: string, interval?: string) => Promise<StartCheckoutResult>;
  readonly removeSlot: () => Promise<MutationResult>;
  readonly openPortal: () => Promise<PortalResult>;
};

const failure = async (res: Response, fallback: string): Promise<{ ok: false; message: string }> => {
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  return { ok: false, message: body?.message ?? `${fallback} (${res.status})` };
};

// The caller's own billing for one tenant (IP-352): the summary plus the slot
// purchase / release / portal actions. Buying a slot is pay-first -- it returns
// a hosted Stripe Checkout URL and the slot is granted by the webhook after
// payment (so nothing changes in our state until then). Releasing a slot is an
// immediate credit (write-through). Stripe stays the source of truth.
export const useCustomerBilling = (tenantId: string): UseCustomerBillingResult => {
  const { status, data, error, isFetching, refetch, mutate } = useCachedQuery<TenantBillingSummary>({
    cacheResource: `billing:${tenantId}`,
    queryKey: billingUrl(tenantId),
    fetcher: async (signal) => {
      const res = await fetch(billingUrl(tenantId), { credentials: "include", signal });
      if (!res.ok) throw new Error(`billing failed (${res.status})`);
      return (await res.json()) as TenantBillingSummary;
    },
    refreshIntervalMs: 0,
  });

  const startSlotCheckout = useCallback(
    // interval is the slot's billing cadence (IP-389, "month"/"year");
    // undefined lets the backend default to the base plan's.
    async (returnTo: string, interval?: string): Promise<StartCheckoutResult> => {
      const res = await fetch(`${billingUrl(tenantId)}/slots/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ returnTo, interval }),
      });
      if (!res.ok) return failure(res, "could not start checkout");
      const body = (await res.json()) as { url: string };
      return { ok: true, url: body.url };
    },
    [tenantId],
  );

  const removeSlot = useCallback(async (): Promise<MutationResult> => {
    const res = await fetch(`${billingUrl(tenantId)}/slots`, {
      method: "DELETE",
      credentials: "include",
      headers: getCsrfHeader(),
    });
    if (!res.ok) return failure(res, "request failed");
    const summary = (await res.json()) as TenantBillingSummary;
    mutate(() => summary);
    return { ok: true };
  }, [tenantId, mutate]);

  const openPortal = useCallback(async (): Promise<PortalResult> => {
    const res = await fetch(`${billingUrl(tenantId)}/portal-session`, {
      method: "POST",
      credentials: "include",
      headers: getCsrfHeader(),
    });
    if (!res.ok) return failure(res, "portal session failed");
    const body = (await res.json()) as { url: string };
    return { ok: true, url: body.url };
  }, [tenantId]);

  return { status, summary: data, error, isFetching, refetch, startSlotCheckout, removeSlot, openPortal };
};

// Open a Stripe-hosted URL (the slot-purchase Checkout, or the billing portal)
// in a NEW tab. The blank tab is opened synchronously inside the click handler
// (before the await) so the browser treats it as user-initiated and doesn't
// block it; it's pointed at the Stripe URL once the session is minted. If the
// popup was blocked (tab is null) we fall back to a same-tab redirect so the
// action still works. On error the blank tab is closed. The opener tab refreshes
// itself on focus (useCachedQuery revalidates on visibilitychange).
export const openStripeUrlInNewTab = async (
  getUrl: () => Promise<{ ok: true; url: string } | { ok: false; message: string }>,
): Promise<MutationResult> => {
  const tab = window.open("", "_blank");
  const result = await getUrl();
  if (!result.ok) {
    tab?.close();
    return { ok: false, message: result.message };
  }
  if (tab) {
    tab.location.href = result.url;
  } else {
    window.location.href = result.url;
  }
  return { ok: true };
};

// After returning from a slot-purchase Checkout (?slot=success), the webhook
// grant lands a moment later -- poll the billing summary a few times so the new
// integration slot shows without a manual refresh, then strip the query param.
// Returns true while still polling (so a page can show a "processing" note).
export const useSlotReturnPoll = (refetch: () => Promise<void>): boolean => {
  const [pending, setPending] = useState<boolean>(
    () => new URLSearchParams(window.location.search).get("slot") === "success",
  );
  useEffect(() => {
    if (!pending) return;
    let tries = 0;
    void refetch();
    const id = window.setInterval(() => {
      tries += 1;
      void refetch();
      if (tries >= 5) {
        window.clearInterval(id);
        setPending(false);
        const url = new URL(window.location.href);
        url.searchParams.delete("slot");
        window.history.replaceState({}, "", url.toString());
      }
    }, 2500);
    return () => window.clearInterval(id);
  }, [pending, refetch]);
  return pending;
};
